import { useState } from 'react';
import type {
  DateFilterValue,
  FilterOp,
  FilterState,
  FilterValue,
  ListFilterValue,
  ListOp,
  NumericFilterValue,
} from './filterTypes';

export interface FilterOperatorDefaults {
  /** Default comparison operator for date and numeric fields. Defaults to 'eq'. */
  op?: FilterOp;
  /** Default inclusion operator for list fields. Defaults to 'in'. */
  listOp?: ListOp;
}

export interface FilterOperators<T> {
  getFilterOp: (key: keyof T) => FilterOp;
  setFilterOp: (key: keyof T, op: FilterOp) => void;
  getListOp: (key: keyof T) => ListOp;
  setListOp: (key: keyof T, op: ListOp) => void;
}

/**
 * Manages comparison and list operators independently of any panel component.
 *
 * When the operator changes for a field that already has a value, the hook
 * re-emits onFilterChange so the active filter stays in sync with the new op.
 *
 * Usage:
 *   const operators = useFilterOperators(filterState, onFilterChange);
 *   // optionally override defaults:
 *   const operators = useFilterOperators(filterState, onFilterChange, { op: 'gte', listOp: 'not_in' });
 */
export function useFilterOperators<T>(
  filterState: FilterState<T>,
  onFilterChange: (key: keyof T, value: FilterValue) => void,
  defaults: FilterOperatorDefaults = {},
): FilterOperators<T> {
  const defaultOp: FilterOp = defaults.op ?? 'eq';
  const defaultListOp: ListOp = defaults.listOp ?? 'in';

  const [filterOps, setFilterOps] = useState<Partial<Record<keyof T, FilterOp>>>({});
  const [listOps, setListOps] = useState<Partial<Record<keyof T, ListOp>>>({});

  const getFilterOp = (key: keyof T): FilterOp => filterOps[key] ?? defaultOp;
  const getListOp = (key: keyof T): ListOp => listOps[key] ?? defaultListOp;

  const setFilterOp = (key: keyof T, op: FilterOp) => {
    setFilterOps(prev => ({ ...prev, [key]: op }));
    const current = filterState[key] as DateFilterValue | NumericFilterValue | undefined;
    if (current) onFilterChange(key, { ...current, op });
  };

  const setListOp = (key: keyof T, op: ListOp) => {
    setListOps(prev => ({ ...prev, [key]: op }));
    const current = filterState[key] as ListFilterValue | undefined;
    if (current) onFilterChange(key, { ...current, op });
  };

  return { getFilterOp, setFilterOp, getListOp, setListOp };
}
